import { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useInactiveUpdate } from 'apps/studio/lib/hooks/useInactiveUpdate'
import {
  activeTableLayoutSelector,
  elementsSelector,
  activePageSelector,
} from 'apps/studio/store/selectors/pdf'
import {
  activeSheetSelector,
  entriesSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { alphaEntryTypesSelector } from 'apps/formsCreationKit/store/selectors/types'
import { pdfPageMetaByPageNumberSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import {
  buildBulkAddColumnsPayload,
  buildBulkDeleteColumnsPayload,
  buildBulkAddRowsPayload,
  buildBulkDeleteRowsPayload,
  buildTextEntryType,
  resolveTableSheets,
  entriesForTableSheets,
  getColumnCapacity,
  MAX_TABLE_ROWS,
} from 'apps/studio/lib/utils/entities'
import {
  addTableColumn,
  deleteTableColumn,
  addTableRow,
  deleteTableRow,
} from 'apps/studio/store/actions/sharedActions'
import { CancelConfirmDialog } from 'apps/studio/components/dialogs/CancelConfirmDialog'

const MIN_DIMENSION_COUNT = 2

// Inner component isolated so we can remount it (via key) to reset
// useInactiveUpdate's internal state when the confirmation dialog is cancelled.
const DimensionInput = ({ id, currentCount, onUpdate, min, max }) => {
  const { value, onChange, onBlur, onKeyUp } = useInactiveUpdate(
    currentCount,
    false,
    onUpdate,
    { min, max }
  )
  return (
    <input
      id={id}
      type="number"
      className="input-fields__input"
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onKeyUp={onKeyUp}
    />
  )
}

export const SettingsTableDimensionInput = ({
  property,
  label,
  id,
  data,
}) => {
  const dispatch = useDispatch()
  const [pendingCount, setPendingCount] = useState(null)
  // Bumped on cancel so DimensionInput remounts and useInactiveUpdate resets.
  const [resetKey, setResetKey] = useState(0)

  const currentCount = data[property] ?? 0

  const tableLayout = useSelector(activeTableLayoutSelector)
  const activeSheet = useSelector(activeSheetSelector)
  const allSheets = useSelector(sheetsSelector)
  const allEntries = useSelector(entriesSelector)
  const allElements = useSelector(elementsSelector)
  const entryTypes = useSelector(alphaEntryTypesSelector)
  const activePage = useSelector(activePageSelector)
  const pageMeta = useSelector(pdfPageMetaByPageNumberSelector(activePage?.page_number))
  const pageWidth = pageMeta?.originalWidth ?? 0

  const isColumn = property === 'column_count'
  const dimensionName = isColumn ? 'column' : 'row'

  // Columns are capped by the hard limit and by the page's right edge; rows by
  // MAX_TABLE_ROWS. Never dip below the current count so an oversized table
  // (e.g. dragged toward the right edge) still reports its real size.
  const maxCount = isColumn
    ? Math.max(currentCount, getColumnCapacity({ tableLayout, pageWidth }))
    : Math.max(currentCount, MAX_TABLE_ROWS)

  const getColumnContext = useCallback(() => {
    if (!tableLayout || !activeSheet) return null
    const { keyFieldSheet, tableSheet } = resolveTableSheets(
      allSheets,
      activeSheet.section_id
    )
    if (!tableSheet) return null
    const tableEntries = entriesForTableSheets(allEntries, keyFieldSheet, tableSheet)
    const headerElements = allElements.filter(el => el.layout_id === tableLayout.id)
    const textEntryType = buildTextEntryType(entryTypes)
    return { tableSheet, tableEntries, headerElements, textEntryType }
  }, [tableLayout, activeSheet, allSheets, allEntries, allElements, entryTypes])

  const applyChange = useCallback(
    targetCount => {
      if (!tableLayout || targetCount === currentCount) return
      const delta = targetCount - currentCount

      if (isColumn) {
        const ctx = getColumnContext()
        if (!ctx) return
        const { tableSheet, tableEntries, headerElements, textEntryType } = ctx

        if (delta > 0) {
          const payload = buildBulkAddColumnsPayload({
            tableLayout,
            tableSheet,
            entries: tableEntries,
            headerElements,
            count: delta,
            pageWidth,
            entryType: textEntryType,
          })
          if (payload) dispatch(addTableColumn(payload))
        } else {
          const payload = buildBulkDeleteColumnsPayload({
            tableLayout,
            tableSheet,
            entries: tableEntries,
            headerElements,
            count: -delta,
          })
          if (payload) dispatch(deleteTableColumn(payload))
        }
      } else {
        if (delta > 0) {
          const payload = buildBulkAddRowsPayload({ tableLayout, count: delta })
          if (payload) dispatch(addTableRow(payload))
        } else {
          const payload = buildBulkDeleteRowsPayload({ tableLayout, count: -delta })
          if (payload) dispatch(deleteTableRow(payload))
        }
      }
    },
    [tableLayout, currentCount, isColumn, getColumnContext, pageWidth, dispatch]
  )

  const handleUpdate = useCallback(
    value => {
      const parsed = parseInt(value, 10)
      if (isNaN(parsed) || parsed < MIN_DIMENSION_COUNT) return
      // Asking for more than fits snaps to what the page can actually hold.
      const target = maxCount ? Math.min(parsed, maxCount) : parsed
      if (target === currentCount) {
        // Nothing to change, but the input may still be showing the raw
        // out-of-range value the user typed (e.g. 600 when capped at 100).
        // Remount to resync it back to currentCount.
        setResetKey(k => k + 1)
        return
      }

      if (target < currentCount) {
        setPendingCount(target)
      } else {
        applyChange(target)
      }
    },
    [currentCount, applyChange, maxCount]
  )

  const handleConfirm = () => {
    if (pendingCount !== null) applyChange(pendingCount)
    setPendingCount(null)
  }

  const handleCancel = () => {
    setPendingCount(null)
    // Remount DimensionInput so useInactiveUpdate resets to currentCount.
    setResetKey(k => k + 1)
  }

  const deleteCount = pendingCount !== null ? currentCount - pendingCount : 0

  return (
    <div className="gcds--input-fields input--secondary input--small settings-number-input-wrapper">
      <label htmlFor={id} className="input-fields__label">
        {label}
      </label>
      <div className="input-fields--wrapper">
        <DimensionInput
          key={resetKey}
          id={id}
          currentCount={currentCount}
          onUpdate={handleUpdate}
          min={MIN_DIMENSION_COUNT}
          max={maxCount}
        />
      </div>
      {pendingCount !== null && (
        <CancelConfirmDialog
          title={`Delete ${dimensionName}s?`}
          description={`You currently have ${currentCount} ${dimensionName}s, do you want to delete ${deleteCount}?`}
          confirmText={`Yes, delete ${deleteCount} ${dimensionName}${deleteCount !== 1 ? 's' : ''}`}
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  )
}
